import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import PicklistField from 'core/components/validatedForm/DropdownField'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import useParams from 'core/hooks/useParams'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { createVirtualMachine, getVirtualMachine, snapshotVirtualMachine } from './actions'
import { getManagedNetworkColumns, getSimpleNetworkColumns } from './columns/networkColumns'
import {
  NetworkMode,
  MANAGED_NETWORK_INFO,
  SIMPLE_NETWORK_TOOLTIP_INFO,
} from '../networks/constants'
import ModalForm from 'core/elements/modal/ModalForm'
import Radio from 'core/elements/input/Radio'
import Tooltip from 'core/elements/tooltip/Tooltip'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { Route } from 'core/plugins/route'
import { humanReadableSize } from 'openstack/helpers'
import Text from 'core/elements/Text'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { requiredValidator, yamlValidator, validIpv4Validator } from 'core/utils/fieldValidators'
import { keys, omit, prop } from 'ramda'
import useScopedPreferences from 'core/session/useScopedPreferences'
import ServiceUnhealthyInfo from '../common/ServiceUnhealthyInfo'
import moment from 'moment'
import { waitSeconds } from 'openstack/components/infrastructure/blueprint/helpers'
import { deleteImage, getImagesWithName } from 'openstack/components/images/actions'
import useListAction from 'core/hooks/useListAction'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import { listFlavors } from '../flavors/actions'
import { listNetworks } from '../networks/actions'
import { listSshKeys } from 'openstack/components/security/keys/actions'
import { flavorsSelector } from '../flavors/selectors'
import { networksSelector } from '../networks/selectors'
import { sshKeysSelector } from 'openstack/components/security/keys/selectors'
import PaginatedSelectableCards from 'core/components/PaginatedSelectableCards'
import SelectableCard from 'core/components/SelectableCard'
import ListTableField from 'core/components/validatedForm/ListTableField'
import { noop, stopPropagation } from 'utils/fp'
import Dropdown from 'core/elements/dropdown'
import TextField from 'core/components/validatedForm/TextField'
import {
  getPrivateIpValidator,
  ErrorText,
  placeholderCloudInitYaml,
  hotplugMemoryValidator,
  hotplugVcpuValidator,
} from './CreateVirtualMachineModal'
import SshKeyPicklist from './SshKeyPicklist'
import CodeMirror from 'core/components/validatedForm/CodeMirrorField'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import ClusterHostGroupsPicklist from '../infrastructure/clusters/ClusterHostGroupsPicklist'
import { isHotpluggableFlavor, isOpenCloud } from 'core/utils/helpers'
import { isSelfServiceRole } from 'k8s/util/helpers'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Alert from 'core/components/Alert'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import ExternalLink from 'core/components/ExternalLink'
import { routeManager } from 'core/utils/routes'
import { ImagePageTabs } from '../images/model'
import VmCredentialsPanel from './VmCredentialsPanel'
import VmSnapshotInfo from './VmSnapshotInfo'
import { SharedWithTenants } from '../networks/NetworksListPage'
import { formatTimestampToLocal, isVolumeBackedVm } from './helpers'
import { getVolumeSnapshot } from '../storage/snapshots/actions'
import useSimpleNetworkPorts from './hooks/useSimpleNetworkPorts'
import { buildVMRequestBody, LAYER_2_PORT_METADATA_KEY } from './utils/vmBodyBuilder'

const useStyles = makeStyles<Theme>((theme) => ({
  pairs: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    justifyContent: 'space-between',
  },
  // This CSS is weird, is a workaround
  card: {
    height: 192,
    '& .card-body': {
      display: 'grid',
      height: 160,
    },
  },
  cardHeader: {
    fontSize: 20,
  },
  cardText: {
    fontSize: 16,
  },
  successIcon: {
    color: theme.components.badge.success.color,
    marginRight: 8,
  },
  pendingIcon: {
    color: theme.components.badge.warning.color,
    marginRight: 8,
  },
  failedIcon: {
    color: theme.components.badge.error.color,
    marginRight: 8,
  },
  passwordScreen: {
    display: 'grid',
    gap: 24,
  },
  errorMessage: {
    display: 'grid',
    gridTemplateColumns: '26px auto',
    alignItems: 'center',
  },
  errorText: {
    color: theme.components.graph.error,
  },
  portSelection: {
    display: 'grid',
    gridTemplateColumns: '240px auto',
  },
  fixedIPInput: {
    gap: 0,
  },
  cloneProgress: {
    display: 'grid',
    gap: 16,
    padding: 16,
  },
  networkModeCards: {
    display: 'flex',
    gap: theme.spacing(4),
    marginBottom: 16,
  },
  networkCardWrapper: {
    position: 'relative',
    width: '100%',
  },
  networkCard: {
    width: '100%',
    height: '100%',
  },
  networkCardBody: {
    display: 'grid',
    gridGap: theme.spacing(1),
    gridTemplateColumns: 'max-content 1fr max-content',
    padding: '16px',
    placeItems: 'center',
  },
  networkCardContent: {
    justifySelf: 'left',
  },
  networkInfoIcon: {
    color: theme.palette.grey[500],
    fontSize: '20px',
    cursor: 'help',
  },
  networkTooltipBody: {
    maxWidth: 250,
  },
  progressTable: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content max-content',
    gap: 16,
    padding: `0px 16px`,
    alignItems: 'center',
  },
  text: {
    marginBottom: 16,
  },
  centered: {
    textAlign: 'center',
  },
  errorMessageContainer: {
    marginLeft: theme.spacing(1),
  },
}))

interface Props {
  cloneRoute: Route
}

const progressIconByStatus = {
  success: 'check',
  pending: 'circle-notch',
  failed: 'xmark',
}

const SubnetsCellComponent = ({ value }) => {
  return (
    <>
      {value.map((subnet) => (
        <Text key={subnet.name} variant="body2">
          {subnet.name}: <b>{subnet.cidr}</b>
        </Text>
      ))}
    </>
  )
}

const cloneVmFromImage = async ({
  vm,
  snapshotFn,
  createFn,
  deleteFn, // injected delete action
  setCloneProgressDetails,
  setVmPassword,
  setSnapshotDeleteError, // new setter for error state
  params,
  preCreatePortsFn, // Simple Network port creation
  cleanupPortsFn, // Simple Network port cleanup
  currentTenant, // Current tenant ID
}) => {
  const snapshotName = `${vm?.name}-${moment().format('YYYY-MM-DD-HH:mm:ss')}`
  const snapshotBody = { createImage: { name: snapshotName } }
  const { success: snapshotVmSuccess } = await snapshotFn({ id: vm.id, body: snapshotBody })
  if (!snapshotVmSuccess) {
    setCloneProgressDetails([
      { step: 1, status: 'failed', label: 'VM Snapshot Initiated' },
      { step: 2, status: 'failed', label: 'VM Snapshot Ready' },
      { step: 3, status: 'failed', label: 'VM Creation Initiated' },
      { step: 4, status: 'failed', label: 'VM Snapshot Cleanup' },
    ])
    return false
  }

  setCloneProgressDetails([
    { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
    { step: 2, status: 'pending', label: 'VM Snapshot Ready' },
    { step: 3, status: 'pending', label: 'VM Creation Initiated' },
    { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
  ])

  // 🔹 Poll for snapshot readiness
  let snapshotId = ''
  let snapshotReady = false
  while (!snapshotReady) {
    const images = await getImagesWithName(snapshotName)
    const image = images?.[0]
    if (!image) {
      // snapshot not visible yet, keep waiting
      await waitSeconds(5)
      continue
    }
    snapshotId = image?.id

    if (image?.status === 'active') {
      // If booted from volume, ensure all image's
      // dependent snapshots are available
      if (image?.block_device_mapping) {
        try {
          const blockStorage = image.block_device_mapping
            ? JSON.parse(image.block_device_mapping)
            : null
          if (blockStorage) {
            const snapshotIds = blockStorage
              .filter((vol) => vol.source_type === 'snapshot')
              .map((vol) => vol.snapshot_id)
            const promises = snapshotIds?.map((snapshotId) => getVolumeSnapshot(snapshotId))
            const responses = await Promise.allSettled(promises)
            const allSnapshotsAvailable = responses?.every(
              (response: any) => response?.value?.status === 'available',
            )
            if (!allSnapshotsAvailable) {
              await waitSeconds(5)
              continue
            }
          }
        } catch (e) {
          console.error(e)
        }
      }
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'success', label: 'VM Snapshot Ready' },
        { step: 3, status: 'pending', label: 'VM Creation Initiated' },
        { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
      ])
      snapshotReady = true
    } else if (['queued', 'saving'].includes(image?.status)) {
      // still in progress
      await waitSeconds(5)
    } else {
      // unexpected error status (killed, error, deleted, etc.)
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'failed', label: 'VM Snapshot Ready' },
        { step: 3, status: 'failed', label: 'VM Creation Initiated' },
        { step: 4, status: 'failed', label: 'VM Snapshot Cleanup' },
      ])
      return false
    }
  }

  // Pre-create ports for Simple Networks (L2-only networks without IP pools)
  let preCreatedPorts: any = {}
  const hasSimpleNetworks = params.networks.some((network) => network.isSimpleNetwork)

  if (hasSimpleNetworks) {
    try {
      preCreatedPorts = await preCreatePortsFn(
        params.networks,
        1, // Single VM clone
        currentTenant,
        params.name,
      )
      console.log('[Clone] Simple Network ports created:', preCreatedPorts)
    } catch (error) {
      console.error('[Clone] Simple Network port pre-creation failed:', error)
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'success', label: 'VM Snapshot Ready' },
        { step: 3, status: 'failed', label: 'VM Creation Initiated' },
        { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
      ])
      return false
    }
  }

  // 🔹 VM Creation
  const metadata = params.metadata.reduce(
    (accum, pair) => ({ ...accum, [pair.key]: pair.value }),
    {},
  )
  const hotplugMetadata = params.enableHotplugging
    ? {
        HOTPLUG_MEMORY: String(params.hotplugMemory),
        HOTPLUG_CPU: String(params.hotplugVcpu),
      }
    : {}

  // Add Simple Network port IDs to metadata for cleanup on VM deletion
  const preCreatedPortIds =
    Object.keys(preCreatedPorts)?.length > 0 ? Object.values(preCreatedPorts)?.join(',') : undefined

  const combinedMetadata = {
    ...metadata,
    ...hotplugMetadata,
    // For VMs with Simple Network ports, store port IDs in metadata for cleanup
    ...(preCreatedPortIds && { [LAYER_2_PORT_METADATA_KEY]: preCreatedPortIds }),
  }

  // Check if config_drive should be auto-enabled
  // Config drive is needed when cloud-init is enabled AND Simple Networks are used
  // (Simple Networks don't have DHCP, so cloud-init needs config_drive to work)
  const shouldEnableConfigDrive =
    params.useCloudInit &&
    params.networks.some(
      (network) =>
        network.isSimpleNetwork ||
        network.subnetDetails?.some((subnet: any) => subnet.enable_dhcp === false),
    )

  // Encode cloud-init script if enabled
  const encodedScript = params.useCloudInit ? btoa(params.cloudInitYaml) : undefined

  const vmBody = {
    server: {
      imageRef: snapshotId,
      flavorRef: params.flavor?.id,
      name: params.name,
      networks: params.networks.map((network) => {
        // For Simple Networks, use pre-created port
        if (network.isSimpleNetwork && preCreatedPorts[network.id]) {
          return {
            port: preCreatedPorts[network.id],
          }
        }
        // For Managed Networks, use standard port/IP configuration
        return {
          uuid: network.id,
          // When port is selected as fixedIP, use fixedIP
          // When any other port is selected, use portId
          ...(params.selectedPorts[network.id]
            ? params.selectedPorts[network.id] === 'fixedIP'
              ? { fixed_ip: params.fixedIPs[network.id] }
              : { port: params.selectedPorts[network.id] }
            : {}),
        }
      }),
      key_name: params?.sshKey || undefined,
      config_drive: shouldEnableConfigDrive,
      user_data: params.useCloudInit ? encodedScript : undefined,
      security_groups: vm?.security_groups,
      metadata: combinedMetadata,
      ...(params.availabilityZone && { availability_zone: params.availabilityZone }),
      // availability_zone: vm?.['OS-EXT-AZ:availability_zone']
      //   ? vm?.['OS-EXT-AZ:availability_zone']
      //   : undefined,
    },
    // 'OS-SCH-HNT:scheduler_hints': vm?.serverGroup
    //   ? {
    //       group: vm?.serverGroup?.id,
    //     }
    //   : undefined,
  }

  // 🔹 Create VM and handle VM polling
  let vmInfo
  let createSuccessFlag = false
  try {
    const { success: createSuccess, response } = await createFn({ body: vmBody })
    createSuccessFlag = createSuccess
    vmInfo = response
    let retryCount = 0 // To track the number of retries
    const maxRetries = 5 // To avoid infinite loops in case of persistent issues
    if (createSuccess) {
      // Poll VM if state is 'building'
      while (vmInfo['OS-EXT-STS:vm_state'] === 'building' && retryCount < maxRetries) {
        setCloneProgressDetails([
          { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
          { step: 2, status: 'success', label: 'VM Snapshot Ready' },
          { step: 3, status: 'pending', label: 'VM Creation Initiated' },
          { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
        ])
        await waitSeconds(5)
        try {
          const result = await getVirtualMachine({ id: response.id })
          if (result) vmInfo = result
        } catch (err) {
          console.error('VM polling failed:', err)
          // Continue retrying — don't break; just count as a failed attempt
        }
        retryCount++
      }

      // VM ready
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'success', label: 'VM Snapshot Ready' },
        { step: 3, status: 'success', label: 'VM Creation Initiated' },
        { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
      ])

      setVmPassword(response?.adminPass)
    } else {
      // VM creation failed
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'success', label: 'VM Snapshot Ready' },
        { step: 3, status: 'failed', label: 'VM Creation Initiated' },
        { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
      ])
    }
  } finally {
    // 🔹 Cleanup Simple Network ports on failure
    if (!createSuccessFlag && hasSimpleNetworks && preCreatedPorts) {
      try {
        console.log('[Clone] Cleaning up Simple Network ports due to failure...')
        await cleanupPortsFn(preCreatedPorts)
      } catch (cleanupError) {
        console.error('[Clone] Port cleanup failed:', cleanupError)
      }
    }

    // 🔹 Always attempt VM Snapshot Cleanup if snapshot creation succeeded
    try {
      const { success: deleteSuccess, error } = await deleteFn({
        id: snapshotId,
      })
      setCloneProgressDetails((prev) => [
        ...prev.slice(0, 3),
        {
          step: 4,
          status: deleteSuccess ? 'success' : 'failed',
          label: 'VM Snapshot Cleanup',
        },
      ])
      if (!deleteSuccess) throw error || new Error('VM Snapshot Cleanup failed')
    } catch (err) {
      setSnapshotDeleteError(err)
    }
  }

  return createSuccessFlag
}

export default function CloneVirtualMachineModal({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const classes = useStyles()
  const [cloningInProgress, setCloningInProgress] = useState(false)
  const [showCloneProgress, setShowCloneProgress] = useState(false)
  const [cloneProgressDetails, setCloneProgressDetails] = useState([
    { step: 1, status: 'pending', label: 'VM Snapshot Initiated' },
    { step: 2, status: 'pending', label: 'VM Snapshot Ready' },
    { step: 3, status: 'pending', label: 'VM Creation Initiated' },
    { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
  ])
  const [loadedDefaultFlavor, setLoadedDefaultFlavor] = useState(false)
  const [defaultFlavor, setDefaultFlavor] = useState(null)
  const [loadedDefaultNetworks, setLoadedDefaultNetworks] = useState(false)
  const [loadedDefaultSshKey, setLoadedDefaultSshKey] = useState(false)
  const [showVmPassword, setShowVmPassword] = useState(false)
  const [vmPassword, setVmPassword] = useState('')
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const [vmNetworkMode, setVmNetworkMode] = useState<NetworkMode | null>(null)
  const isHotPluggedVm = !!vm?.metadata?.['HOTPLUG_MEMORY']
  const defaultParams = {
    name: `${vm?.name}-clone`,
    enableHotplugging: isHotPluggedVm,
    image: null,
    flavor: null,
    selectedPorts: {},
    fixedIPs: {},
    networks: [],
    availabilityZone: vm?.['OS-EXT-AZ:availability_zone'],
    sshKey: null,
    useCloudInit: false,
    cloudInitYaml: placeholderCloudInitYaml,
    hotplugMemory: vm?.metadata?.['HOTPLUG_MEMORY'] ? vm.metadata['HOTPLUG_MEMORY'] : null,
    hotplugVcpu: vm?.metadata?.['HOTPLUG_CPU'] ? vm.metadata['HOTPLUG_CPU'] : null,
    metadata: vm?.metadata
      ? Object.entries(
          omit(
            [
              'HOTPLUG_MEMORY',
              'HOTPLUG_MEMORY_MAX',
              'HOTPLUG_CPU',
              'HOTPLUG_CPU_MAX',
              LAYER_2_PORT_METADATA_KEY, // Exclude original VM's port IDs
            ],
            vm.metadata,
          ),
        ).map(([key, value]) => {
          return { key, value }
        })
      : [],
  }

  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs

  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const isSelfServiceUser = isSelfServiceRole(session)

  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const { loading: loadingFlavors } = useListAction(listFlavors, {
    initialLoadingState: true,
  })
  const flavors = useSelectorWithParams(flavorsSelector, {} as any)

  const { loading: loadingNetworks } = useListAction(listNetworks, {
    initialLoadingState: true,
  })
  const _networksList = useSelectorWithParams(networksSelector, {} as any)
  const networksList = _networksList.filter((network) => {
    // Include Simple Networks (L2-only, no subnets) AND Managed Networks (with subnets)
    const isManagedNetwork = network.subnets?.length > 0
    const isSimpleNet = network.isSimpleNetwork

    // Must be either Managed or Simple network
    if (!isManagedNetwork && !isSimpleNet) return false

    // If network is shared, show it
    if (network.shared) return true

    // For networks in current tenant
    if (network.tenant_id === currentTenant) {
      // For self service users, hide external networks
      if (isSelfServiceUser) return !network?.['router:external']
      // For non-self service users, show all networks
      return true
    }

    // Hide networks not in current tenant
    return false
  })

  // Calculate network counts for toggle cards
  const managedNetworksCount = useMemo(() => {
    return networksList.filter((network) => !network.isSimpleNetwork && network.subnets?.length > 0)
      .length
  }, [networksList])

  const simpleNetworksCount = useMemo(() => {
    return networksList.filter((network) => network.isSimpleNetwork).length
  }, [networksList])

  const { loading: loadingSshKeys } = useListAction(listSshKeys, {
    initialLoadingState: true,
  })
  const sshKeys = useSelector(sshKeysSelector)

  const { update: snapshotVm } = useUpdateAction(snapshotVirtualMachine)
  const { update: createVm, updating, error, reset } = useUpdateAction(createVirtualMachine)
  const { update: deleteSnapshotFn } = useUpdateAction(deleteImage)
  const [snapshotDeleteError, setSnapshotDeleteError] = useState(null)
  const responseError = error

  // Simple Network port management hook
  const { preCreateSimpleNetworkPorts, cleanupPreCreatedPorts } = useSimpleNetworkPorts()

  const submitForm = useCallback(async () => {
    setAttemptedSubmit(true)
    setShowCloneProgress(true)
    setCloningInProgress(true)
    const cloneSuccess = await cloneVmFromImage({
      vm,
      snapshotFn: snapshotVm,
      createFn: createVm,
      deleteFn: deleteSnapshotFn,
      setCloneProgressDetails,
      setVmPassword,
      setSnapshotDeleteError,
      params: params,
      preCreatePortsFn: preCreateSimpleNetworkPorts,
      cleanupPortsFn: cleanupPreCreatedPorts,
      currentTenant,
    })
    setCloningInProgress(false)
    if (cloneSuccess) {
      setShowCloneProgress(false)
      setShowVmPassword(true)
    }
  }, [params, vm, preCreateSimpleNetworkPorts, cleanupPreCreatedPorts, currentTenant])

  const handleClose = (success?: boolean) => {
    setParams(defaultParams)
    setSnapshotDeleteError(null)
    setShowCloneProgress(false)
    setShowVmPassword(false)
    setVmPassword('')
    reset()
    onClose(success)
  }

  useEffect(() => {
    if (loadingFlavors || loadedDefaultFlavor) {
      return
    }
    const vmFlavor = vm?.flavor
    const defaultFlavor = flavors.find((flavor) => {
      return (
        vmFlavor?.original_name === flavor.name &&
        vmFlavor?.disk === flavor.disk &&
        // For hotplugged VMs, we don't want to match the RAM and vCPUs, as flavors will always show 0 vCPUs and 0 RAM
        (!isHotPluggedVm ? vmFlavor?.ram === flavor.ram && vmFlavor?.vcpus === flavor.vcpus : true)
      )
    })

    updateParams({ flavor: defaultFlavor })
    setDefaultFlavor(defaultFlavor)
    setLoadedDefaultFlavor(true)
  }, [loadingFlavors, flavors, vm])

  // Flag to determine if VM is volume backed
  const isVolumeBackedVM = useMemo(() => isVolumeBackedVm(vm), [vm])

  // Want to show the default selected flavor as the first on the list
  const orderedFlavors = useMemo(() => {
    // if hotplugging is enabled, only show flavors with 0 vcpus and 0 ram
    if (params?.enableHotplugging) {
      const hotpluggableFlavors = flavors.filter((flavor) => {
        return isHotpluggableFlavor(flavor)
      })
      updateParams({
        flavor: isHotPluggedVm ? defaultFlavor : hotpluggableFlavors[0],
      })
      return hotpluggableFlavors
    }

    if (defaultFlavor) {
      const restFlavors = flavors.filter(
        (flavor) =>
          flavor.id !== defaultFlavor.id &&
          !isHotpluggableFlavor(flavor) &&
          (isVolumeBackedVM ? flavor?.disk === 0 : flavor?.disk !== 0),
      )
      updateParams({
        flavor: !isHotPluggedVm ? defaultFlavor : restFlavors[0],
      })
      return [...(!isHotPluggedVm ? [defaultFlavor] : []), ...restFlavors]
    } else {
      const restFlavors = flavors.filter(
        (flavor) =>
          !isHotpluggableFlavor(flavor) &&
          (isVolumeBackedVM ? flavor?.disk === 0 : flavor?.disk !== 0),
      )
      updateParams({ flavor: restFlavors[0] })
      return restFlavors
    }
  }, [flavors, defaultFlavor, params?.enableHotplugging, isHotPluggedVm, isVolumeBackedVM])

  useEffect(() => {
    if (loadingNetworks || loadedDefaultNetworks) {
      return
    }
    const vmNetworkNames = keys(vm?.addresses)
    const defaultNetworks = networksList.filter((network) => {
      return vmNetworkNames.includes(network.name)
    })

    // Detect network mode based on default networks
    if (defaultNetworks.length > 0) {
      const hasSimpleNetwork = defaultNetworks.some((network) => network.isSimpleNetwork)
      const hasManagedNetwork = defaultNetworks.some(
        (network) => !network.isSimpleNetwork && network.subnets?.length > 0,
      )

      // Set network mode based on VM's current networks
      if (hasSimpleNetwork && !hasManagedNetwork) {
        setVmNetworkMode(NetworkMode.SIMPLE)
      } else if (hasManagedNetwork && !hasSimpleNetwork) {
        setVmNetworkMode(NetworkMode.MANAGED)
      } else {
        // If mixed or no networks, default to Managed
        setVmNetworkMode(NetworkMode.MANAGED)
      }
    } else {
      // Default to Managed if no networks
      setVmNetworkMode(NetworkMode.MANAGED)
    }

    updateParams({ networks: defaultNetworks })
    setLoadedDefaultNetworks(true)
  }, [loadingNetworks, networksList, vm])

  useEffect(() => {
    if (loadingSshKeys || loadedDefaultSshKey) {
      return
    }
    const sshKeyPresent = sshKeys.some((key) => key.name === vm?.key_name)
    if (sshKeyPresent) {
      updateParams({ sshKey: vm.key_name })
    }
    setLoadedDefaultSshKey(true)
  }, [loadingSshKeys, sshKeys, vm])

  const renderFlavorCard = useCallback(
    (item) => {
      // Flavor must be larger than the image size
      const imageSize = vm?.imageDetails?.virtual_size
      const flavorSize = item.disk * 1024 * 1024 * 1024
      const imageValid = flavorSize === 0 ? true : imageSize ? flavorSize >= imageSize : true
      const disabled = !imageValid
      return (
        <SelectableCard
          id={item}
          onClick={(item) => updateParams({ flavor: item })}
          active={params.flavor?.id === item.id}
          className={classes.card}
          disabled={disabled}
          showCheckmarkIcon
        >
          <Text variant="body2" className={classes.cardHeader}>
            <b>{item.name}</b>
          </Text>
          <br />
          <div className={classes.pairs}>
            <Text variant="body2" className={classes.cardText}>
              vCPUs:
            </Text>
            <Text variant="body2" className={classes.cardText}>
              <b>{item?.vcpus}</b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              RAM:
            </Text>
            <Text variant="body2" className={classes.cardText}>
              <b>{item?.ram && humanReadableSize(item?.ram * 1024 * 1024)}</b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              Disk:
            </Text>
            <Text variant="body2" className={classes.cardText}>
              <b>{item?.disk && humanReadableSize(item?.disk * 1024 * 1024 * 1024)}</b>
              {disabled && ' (Too Small)'}
            </Text>
          </div>
        </SelectableCard>
      )
    },
    [vm, params.flavor],
  )

  // Network columns - use extracted column definitions
  const simpleNetworkColumns = useMemo(() => getSimpleNetworkColumns(), [])

  const managedNetworkColumns = useMemo(
    () => getManagedNetworkColumns({ params, getParamsUpdater }),
    [params.selectedPorts, params.fixedIPs],
  )

  // Select columns based on network mode
  const networkColumns = useMemo(() => {
    return vmNetworkMode === NetworkMode.SIMPLE ? simpleNetworkColumns : managedNetworkColumns
  }, [vmNetworkMode, simpleNetworkColumns, managedNetworkColumns])

  // Filter network list based on selected mode
  const filteredNetworksList = useMemo(() => {
    if (!vmNetworkMode) return []
    if (vmNetworkMode === NetworkMode.SIMPLE) {
      return networksList.filter((network) => network.isSimpleNetwork)
    } else if (vmNetworkMode === NetworkMode.MANAGED) {
      return networksList.filter(
        (network) => !network.isSimpleNetwork && network.subnets?.length > 0,
      )
    }
    return networksList
  }, [vmNetworkMode, networksList])

  // Legacy columns kept for reference (now replaced by dynamic columns above)
  const _legacyNetworkColumns = [
    {
      id: 'name',
      label: 'Name',
    },
    {
      id: 'id',
      label: 'UUID',
      display: false,
    },
    {
      id: 'subnetDetails',
      label: 'Subnets',
      render: (value) => <SubnetsCellComponent value={value} />,
    },
    {
      id: 'ports',
      label: 'Ports',
      Component: ({ row, isSelected }) => {
        const classes = useStyles()
        // Filter out ports that are already bound to a device
        const filteredPorts = row?.ports?.filter(
          (port) =>
            port['binding:vif_type'] === 'unbound' &&
            (port?.device_owner.trim() === '' || !port?.device_owner),
        )

        const options = filteredPorts?.length
          ? [
              { label: 'Automatic', value: '' },
              { label: 'Private IP', value: 'fixedIP' },
              ...filteredPorts.map((port) => {
                const ipAddressString = port.fixed_ips?.map((ip) => ip.ip_address)?.join(', ')
                return {
                  label: `${ipAddressString} (${port.name || port.id})`,
                  value: port.id,
                }
              }),
            ]
          : [
              { label: 'Automatic', value: '' },
              { label: 'Private IP', value: 'fixedIP' },
            ]
        return (
          <div className={classes.portSelection} onClick={isSelected ? stopPropagation : noop}>
            <Dropdown
              name="ports"
              value={params.selectedPorts[row.id] || ''}
              items={options}
              onChange={(value) =>
                getParamsUpdater('selectedPorts')({
                  ...params.selectedPorts,
                  [row.id]: value,
                })
              }
            ></Dropdown>
          </div>
        )
      },
    },
    {
      id: 'fixedIP',
      label: 'Private IP',
      Component: ({ row, isSelected }) => {
        const [fixedIP, setFixedIP] = useState(params.fixedIPs[row.id])
        const isRequired = params.selectedPorts[row.id] === 'fixedIP'
        const selectedPort = row?.ports?.find((port) => port.id === params.selectedPorts[row.id])
        return isRequired ? (
          <div onClick={isSelected ? stopPropagation : noop}>
            <TextField
              id={`${row.id}-fixedIP`}
              placeholder="Enter private IP"
              className={classes.fixedIPInput}
              value={fixedIP}
              onChange={setFixedIP}
              onBlur={() => {
                getParamsUpdater('fixedIPs')({ ...params.fixedIPs, [row.id]: fixedIP })
              }}
              disabled={!isRequired}
              validations={
                isRequired && isSelected
                  ? [validIpv4Validator, requiredValidator, getPrivateIpValidator(row)]
                  : null
              }
            />
          </div>
        ) : selectedPort ? (
          <div>
            {selectedPort?.fixed_ips?.map((ip) => (
              <Text variant="body2">{ip.ip_address}</Text>
            ))}
          </div>
        ) : null
      },
    },
    {
      id: 'port_security_enabled',
      label: 'Port Security',
      render: (val) => (val ? 'Enabled' : 'Disabled'),
      display: false,
    },
    {
      id: 'provider:network_type',
      label: 'Network Type',
      display: false,
    },
    {
      id: 'mtu',
      label: 'MTU',
      display: false,
    },
    {
      id: 'provider:physical_network',
      label: 'Physical Network',
      display: false,
    },
    {
      id: 'router:external',
      label: 'External',
      render: (val) => (val ? 'Yes' : 'No'),
      display: false,
    },
    {
      id: 'provider:segmentation_id',
      label: 'Segmentation ID',
      render: (val) => (val ? val : 'N/A'),
      display: false,
    },
    {
      id: 'admin_state_up',
      label: 'Admin State',
      render: (val) => (val ? 'Up' : 'Down'),
      display: false,
    },
    {
      id: 'dns_domain',
      label: 'DNS Zone',
      render: (val) => (val ? val : '-'),
      display: false,
    },
    {
      id: 'status',
      label: 'Status',
      display: false,
    },
    {
      id: 'availableIps',
      label: 'Available IPs',
      display: false,
    },
    {
      id: 'shared',
      label: 'Shared Tenants',
      render: (value, item) => <SharedWithTenants item={item} value={value} />,
      display: false,
    },
    {
      id: 'created_at',
      label: 'Created',
      render: (time: string) => formatTimestampToLocal(time),
      display: false,
    },
  ] // End of legacy columns

  return (
    <ModalForm
      title={`Clone Virtual Machine`}
      onSubmit={showCloneProgress || showVmPassword ? handleClose : submitForm}
      disableSubmit={showCloneProgress && cloningInProgress}
      onClose={handleClose}
      // submitting={updating}
      error={responseError}
      submitTitle={showCloneProgress || showVmPassword ? `Finish` : `Clone Virtual Machine`}
      maxWidth={1200}
      open
    >
      {!showCloneProgress && !showVmPassword && (
        <>
          <ServiceUnhealthyInfo action="VM Clone" hypervisor imageLibrary blockStorage />
          <VmSnapshotInfo vm={vm} isClonedVM />
          <Text variant="body2" className={classes.text}>
            Clone this virtual machine. A VM Snapshot will be taken and a new VM will be created
            with identical disks and settings. Public IPs will not be assigned.
          </Text>
          <FormFieldSection title="Confirm or Edit your Flavor" step={1}>
            <CheckboxField
              id="enableHotplugging"
              value={params.enableHotplugging}
              label="Enable Hotplugging"
              onChange={getParamsUpdater('enableHotplugging')}
              info="Enable the ability to hotplug VM memory and CPU. Usage of a flavor set to 0 memory and 0 vCPUs is required."
            />
            <Text variant="body2">
              <b>NOTE</b>: When enabling hotplugging, only flavors with 0 RAM and 0 vCPUs will be
              displayed.
            </Text>
            {attemptedSubmit && !params.flavor && <ErrorText>You must select a flavor</ErrorText>}
            <PaginatedSelectableCards
              renderSelectableCardComponent={renderFlavorCard}
              data={orderedFlavors}
              searchProps={['name']}
            />
          </FormFieldSection>
          <FormFieldSection title="Confirm or Edit your Networks" step={2}>
            {/* Select Network Type */}
            <Text
              variant="body2"
              style={{ marginBottom: '16px', display: 'block', fontWeight: '600' }}
            >
              Select Network Type
            </Text>
            <div className={classes.networkModeCards}>
              <div className={classes.networkCardWrapper}>
                <SelectableCard
                  id="managed"
                  onClick={() => {
                    setVmNetworkMode(NetworkMode.MANAGED)
                    // Clear network selection when switching modes
                    if (params.networks.length > 0) {
                      getParamsUpdater('networks')([])
                      getParamsUpdater('fixedIPs')({})
                      getParamsUpdater('selectedPorts')({})
                    }
                  }}
                  active={vmNetworkMode === NetworkMode.MANAGED}
                  className={classes.networkCard}
                  withCustomBody
                >
                  <div className={classes.networkCardBody}>
                    <Radio checked={vmNetworkMode === NetworkMode.MANAGED} />
                    <Text variant="caption1" className={classes.networkCardContent}>
                      Managed Networks ({managedNetworksCount})
                    </Text>
                    <Tooltip
                      customClassName={classes.networkTooltipBody}
                      message={MANAGED_NETWORK_INFO}
                    >
                      <HelpOutlineIcon className={classes.networkInfoIcon} />
                    </Tooltip>
                  </div>
                </SelectableCard>
              </div>
              <div className={classes.networkCardWrapper}>
                <SelectableCard
                  id="simple"
                  onClick={() => {
                    setVmNetworkMode(NetworkMode.SIMPLE)
                    // Clear network selection when switching modes
                    if (params.networks.length > 0) {
                      getParamsUpdater('networks')([])
                      getParamsUpdater('fixedIPs')({})
                      getParamsUpdater('selectedPorts')({})
                    }
                  }}
                  active={vmNetworkMode === NetworkMode.SIMPLE}
                  className={classes.networkCard}
                  withCustomBody
                >
                  <div className={classes.networkCardBody}>
                    <Radio checked={vmNetworkMode === NetworkMode.SIMPLE} />
                    <Text variant="caption1" className={classes.networkCardContent}>
                      Layer 2 Networks ({simpleNetworksCount})
                    </Text>
                    <Tooltip
                      customClassName={classes.networkTooltipBody}
                      message={SIMPLE_NETWORK_TOOLTIP_INFO}
                    >
                      <HelpOutlineIcon className={classes.networkInfoIcon} />
                    </Tooltip>
                  </div>
                </SelectableCard>
              </div>
            </div>

            <ListTableField
              id="networks"
              data={filteredNetworksList}
              loading={loadingNetworks}
              columns={networkColumns}
              onChange={getParamsUpdater('networks')}
              value={params.networks}
              uniqueIdentifier="id"
              searchTargets={['name']}
              multiSelection
              required
            />
          </FormFieldSection>
          <FormFieldSection title="Customize your VM" step={4}>
            <TextField
              id="name"
              label="Name"
              onChange={getParamsUpdater('name')}
              value={params.name}
              info="Choose a name for your VM"
              required
            />
            <PicklistField
              id="availabilityZone"
              label="Cluster"
              info="Choose a cluster for your VM"
              onChange={getParamsUpdater('availabilityZone')}
              value={params.availabilityZone}
              disableClusterWithoutHosts
              fullWidth
              required={isVmware && isOpenCloud() ? false : true}
              DropdownComponent={ClusterHostGroupsPicklist}
            />
            <PicklistField
              id="sshKey"
              DropdownComponent={SshKeyPicklist}
              value={params.sshKey}
              onChange={getParamsUpdater('sshKey')}
              tooltip="Select an SSH key for your VM"
            />
            <CheckboxField
              id="useCloudInit"
              value={params.useCloudInit}
              label="Use cloud-init"
              onChange={getParamsUpdater('useCloudInit')}
            />
            {params.useCloudInit && (
              <CodeMirror
                variant="light"
                label="cloud-init YAML"
                id="cloudInitYaml"
                onChange={getParamsUpdater('cloudInitYaml')}
                value={params.cloudInitYaml}
                // className={fullWidth}
                validations={[yamlValidator]}
              />
            )}
            {params.enableHotplugging && (
              <>
                <TextField
                  id="hotplugMemory"
                  label="Hotplug RAM (MB)"
                  onChange={(val) => updateParams({ hotplugMemory: val })}
                  value={params.hotplugMemory}
                  type="number"
                  min={1}
                  info="The memory that will be hotplugged to the VM."
                  validations={[hotplugMemoryValidator]}
                  required
                />
                <TextField
                  id="hotplugVcpu"
                  label="Hotplug vCPUs"
                  onChange={(val) => updateParams({ hotplugVcpu: val })}
                  value={params.hotplugVcpu}
                  type="number"
                  min={1}
                  info="The number of vCPUs that will be hotplugged to the VM."
                  validations={[hotplugVcpuValidator]}
                  required
                />
              </>
            )}
            <KeyValuesField
              id="metadata"
              label="Metadata"
              value={params.metadata}
              onChange={getParamsUpdater('metadata')}
              addLabel="Add Metadata"
              allowMultipleValues
            />
          </FormFieldSection>
        </>
      )}
      {showCloneProgress && (
        <div className={classes.cloneProgress}>
          <Text variant="body2">
            Cloning is in progress. Please wait and do not close this dialog, or the clone operation
            will be cancelled.
          </Text>
          <div className={classes.progressTable}>
            {cloneProgressDetails.map((step) => (
              <React.Fragment key={step.step}>
                <Text variant="caption1">{step.step}.</Text>
                <Text variant="caption1">{step.label}</Text>
                <Text variant="body2" className={classes.centered}>
                  <FontAwesomeIcon
                    spin={step?.status === 'pending'}
                    className={
                      step?.status === 'success'
                        ? classes.successIcon
                        : step?.status === 'pending'
                        ? classes.pendingIcon
                        : step?.status === 'failed'
                        ? classes.failedIcon
                        : null
                    }
                  >
                    {progressIconByStatus[step?.status]}
                  </FontAwesomeIcon>
                  {/* {step.status} */}
                </Text>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      {showVmPassword && (
        <div className={classes.passwordScreen}>
          {snapshotDeleteError && (
            <Alert
              variant="error"
              title=""
              message={
                <div className={classes.errorMessageContainer}>
                  <ErrorMessage>
                    <Text variant="body2">
                      Failed to cleanup the VM snapshot. Ensure that the image library host is
                      reachable and its certificate is trusted. You may need to delete the VM
                      snapshot manually.{' '}
                      <ExternalLink
                        url={routeManager.routes.openstack.images.path({
                          tab: ImagePageTabs.Snapshots,
                        })}
                      >
                        Click here
                      </ExternalLink>
                    </Text>
                  </ErrorMessage>
                </div>
              }
            />
          )}
          <VmCredentialsPanel vmPassword={vmPassword} />
        </div>
      )}
    </ModalForm>
  )
}
